import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultNoEstatePlanningConsequencesData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'no-estate-planning-consequences-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: '**Delaying or avoiding estate planning can result in excessive taxes, delayed beneficiary access to funds, and unintended beneficiary distributions.** Proper and timely planning is critical to avoid these consequences.',
              },
            },
          ],
        },
      },
      {
        id: 'consequences',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'no-estate-planning-consequences-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'consequence-unintended-beneficiaries',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Unintended beneficiaries',
                      description:
                        'In the absence of a proper estate plan, your estate will be subject to intestate succession laws and there is a risk that unintended beneficiaries, such as estranged relatives or former spouses, might receive a portion of your estate. Additionally, if you want to leave specific possessions to certain individuals, intestate laws may distribute those assets differently, often according to a rigid formula that doesn’t consider personal relationships.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'consequence-excessive-taxes-and-fees',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Excessive taxes and fees',
                      description:
                        'The lack of an estate plan can result in higher taxes and legal fees. Without strategic planning, your heirs might lose a significant portion of their inheritance to federal and state taxes, probate costs, and administrative fees.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'consequence-family-conflicts',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Family conflicts',
                      description:
                        'Without a clear estate plan, disputes among family members can arise due to incapacitation or death, leading to legal battles that can drain the estate’s resources. Conflicts over financial access, asset distribution, guardianship of minor children and healthcare decisions can cause lasting rifts among loved ones.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'consequence-risk-of-mismanagement',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Risk of mismanagement',
                      description:
                        'Thoughtfully considering fiduciaries like financial power of attorney, healthcare agent and estate executor is a critical aspect of estate planning. For example, if you become incapacitated without a financial power of attorney, a court could appoint a guardian to manage your financial affairs. This person may not be someone you trust or who understands your financial wishes, potentially leading to the mismanagement of your assets.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'consequence-delays',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Delayed access to funds',
                      description:
                        'When an estate goes through probate, beneficiaries might not have immediate access to funds or assets, which could create financial hardships, especially if they are relying on those resources to cover living expenses or debts.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
